import { motion } from "motion/react";
import { Rocket, Shield, Heart, Database, Zap, CheckCircle2 } from "lucide-react";

export function PerformanceSection() {
  const performanceFeatures = [
    {
      icon: Rocket,
      title: "Performance Blindada",
      subtitle: "Repository Pattern & Queries Otimizadas",
      description: "Usamos COUNT DISTINCT, prepared statements e limites rígidos para sobrecarga zero no banco de dados.",
      color: "#00F5FF",
      metrics: ["0ms Overhead", "Queries Otimizadas", "Prepared Statements"],
    },
    {
      icon: Database,
      title: "Cache System Inteligente",
      subtitle: "Transients com 15min de Expiração",
      description: "Seus dados estão prontos, não precisamos recalculá-los a cada clique. Cache inteligente garante velocidade máxima.",
      color: "#10B981",
      metrics: ["15min Refresh", "Zero Recalculation", "Instant Load"],
    },
    {
      icon: Shield,
      title: "Bot Detection PhD",
      subtitle: "Proteção Multi-Camadas",
      description: "Verificações de DNSBL, Honeypot, Geo-Firewall e filtros de velocidade. Analytics limpo de sujeira de bots.",
      color: "#FF4D00",
      metrics: ["DNSBL Check", "Honeypot Trap", "Geo-Firewall"],
    },
  ];

  const technicalSpecs = [
    { label: "Arquitetura", value: "MVC Pattern" },
    { label: "Database", value: "Repository Pattern" },
    { label: "Caching", value: "WordPress Transients" },
    { label: "Security", value: "Multi-Layer Detection" },
    { label: "Performance", value: "Optimized Queries" },
    { label: "Monitoring", value: "System Health V32" },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Tech Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,245,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,245,255,0.02)_1px,transparent_1px)] bg-[size:30px_30px]" />

      {/* Animated Circuit Lines */}
      <motion.div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 L50 50 L50 0' stroke='%2300F5FF' stroke-width='1' fill='none'/%3E%3Ccircle cx='50' cy='0' r='3' fill='%2300F5FF'/%3E%3C/svg%3E")`,
        }}
        animate={{
          backgroundPosition: ["0px 0px", "100px 100px"],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Zap className="w-8 h-8 text-[#00F5FF]" />
              <span className="font-['Roboto_Mono'] text-[#00F5FF] text-sm tracking-[0.3em] uppercase">
                Robustez PhD
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-5xl font-bold text-white mb-6">
              Performance Blindada
              <br />
              <span className="text-[#00F5FF] drop-shadow-[0_0_30px_rgba(0,245,255,0.5)]">
                e Bot Detection
              </span>
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="font-['Sora'] text-3xl text-white mb-6">
                "Mas isso vai deixar meu site lento?"
              </p>
              <p className="font-['Sora'] text-4xl font-bold text-[#10B981] drop-shadow-[0_0_30px_rgba(16,185,129,0.5)]">
                Absolutamente não.
              </p>
              <p className="font-['Inter'] text-xl text-gray-300 mt-6">
                Nossa arquitetura foi construída para{" "}
                <span className="text-[#00F5FF] font-semibold">velocidade</span>.
              </p>
            </div>
          </motion.div>

          {/* Performance Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {performanceFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative group"
              >
                <div
                  className="bg-[#0B0E14]/80 backdrop-blur-xl border-2 rounded-2xl p-8 h-full transition-all duration-300 hover:scale-105"
                  style={{
                    borderColor: `${feature.color}40`,
                    boxShadow: `0 0 30px ${feature.color}20, inset 0 0 50px ${feature.color}05`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: `${feature.color}20`,
                      boxShadow: `0 0 30px ${feature.color}50`,
                    }}
                  >
                    <feature.icon className="w-10 h-10" style={{ color: feature.color }} />
                  </div>

                  {/* Title */}
                  <h3
                    className="font-['Sora'] text-2xl font-bold mb-2"
                    style={{ color: feature.color }}
                  >
                    {feature.title}
                  </h3>

                  {/* Subtitle */}
                  <p className="font-['Roboto_Mono'] text-xs text-gray-500 mb-4 uppercase tracking-wider">
                    {feature.subtitle}
                  </p>

                  {/* Description */}
                  <p className="font-['Inter'] text-gray-300 leading-relaxed mb-6">
                    {feature.description}
                  </p>

                  {/* Metrics */}
                  <div className="space-y-2">
                    {feature.metrics.map((metric, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 p-2 rounded-lg"
                        style={{
                          backgroundColor: `${feature.color}10`,
                          border: `1px solid ${feature.color}30`,
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4" style={{ color: feature.color }} />
                        <span className="font-['Roboto_Mono'] text-xs text-gray-400">
                          {metric}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Effect */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${feature.color}25, transparent 70%)`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Technical Specifications */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0B0E14]/60 backdrop-blur-xl border border-[#00F5FF]/30 rounded-3xl p-12 mb-16"
            style={{
              boxShadow: "0 0 60px rgba(0,245,255,0.15), inset 0 0 80px rgba(0,245,255,0.05)",
            }}
          >
            <div className="text-center mb-10">
              <h3 className="font-['Sora'] text-3xl font-bold text-white mb-4">
                Especificações Técnicas PhD
              </h3>
              <p className="font-['Roboto_Mono'] text-sm text-gray-400 max-w-2xl mx-auto">
                A linguagem técnica valida a autoridade do produto para desenvolvedores e donos de sites técnicos.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {technicalSpecs.map((spec, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#0B0E14]/80 border border-[#00F5FF]/20 rounded-xl p-6 text-center hover:border-[#00F5FF]/50 transition-all"
                >
                  <p className="font-['Roboto_Mono'] text-xs text-gray-500 mb-3 uppercase tracking-wider">
                    {spec.label}
                  </p>
                  <p className="font-['Sora'] text-lg font-bold text-[#00F5FF]">
                    {spec.value}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* System Health Monitor */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-[#10B981]/20 to-[#00F5FF]/20 border-2 border-[#10B981] rounded-3xl p-10"
            style={{
              boxShadow: "0 0 60px rgba(16,185,129,0.25), inset 0 0 80px rgba(16,185,129,0.08)",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div
                  className="w-24 h-24 rounded-2xl flex items-center justify-center"
                  style={{
                    backgroundColor: "#10B98130",
                    boxShadow: "0 0 40px rgba(16,185,129,0.4)",
                  }}
                >
                  <Heart className="w-12 h-12 text-[#10B981]" />
                </div>
                <div>
                  <h3 className="font-['Sora'] text-3xl font-bold text-white mb-2">
                    System Health V32
                  </h3>
                  <p className="font-['Inter'] text-lg text-gray-300">
                    Monitora a saúde do próprio plugin para você. Sempre online, sempre otimizado.
                  </p>
                </div>
              </div>

              <div className="text-right">
                <motion.div
                  className="inline-flex items-center gap-3 bg-[#10B981]/20 border border-[#10B981] rounded-xl px-6 py-3"
                  animate={{
                    boxShadow: [
                      "0 0 20px rgba(16,185,129,0.3)",
                      "0 0 40px rgba(16,185,129,0.6)",
                      "0 0 20px rgba(16,185,129,0.3)",
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="w-4 h-4 bg-[#10B981] rounded-full animate-pulse" />
                  <span className="font-['Roboto_Mono'] text-lg font-bold text-[#10B981]">
                    ALL SYSTEMS OPERATIONAL
                  </span>
                </motion.div>
              </div>
            </div>

            {/* Health Metrics */}
            <div className="grid grid-cols-4 gap-6 mt-8">
              {[
                { label: "Database", status: "Optimal", percentage: 98 },
                { label: "Cache", status: "Active", percentage: 100 },
                { label: "Security", status: "Protected", percentage: 100 },
                { label: "Performance", status: "Fast", percentage: 95 },
              ].map((metric, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  className="bg-[#0B0E14]/60 border border-[#10B981]/30 rounded-lg p-4"
                >
                  <p className="font-['Roboto_Mono'] text-xs text-gray-500 mb-2">
                    {metric.label}
                  </p>
                  <p className="font-['Sora'] text-lg font-bold text-[#10B981] mb-2">
                    {metric.status}
                  </p>
                  <div className="h-2 bg-[#0B0E14] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#10B981]"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${metric.percentage}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.6 + i * 0.1 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Closing Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-16"
          >
            <p className="font-['Sora'] text-2xl text-white">
              Neutralizando objeções técnicas com{" "}
              <span className="text-[#00F5FF] font-bold">dados reais</span> e{" "}
              <span className="text-[#10B981] font-bold">arquitetura comprovada</span>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
